import moment from 'moment';
import { computed, ref } from 'vue';
import { TIME_SERIES, TIME_SERIES_DATE_FORMAT, TIME_SERIES_UNIT } from '@/utils/chart';

export default (times, existingTimeSeries, defaultTimeSeries) => {
  const timeSeries = existingTimeSeries || ref(defaultTimeSeries || TIME_SERIES.MONTH_TO_MONTH);
  const timeSeriesUnit = computed(() => TIME_SERIES_UNIT[timeSeries.value]);
  const timeSeriesFormat = computed(() => TIME_SERIES_DATE_FORMAT[timeSeries.value]);

  const timeGroupDataset = computed(() => {
    if (!times.value) {
      return null;
    }

    let values = times.value.values || [];

    if (times.value.calculated) {
      const lastComplete = times.value.calculated.last_complete;
      const lastCompleteIndex = times.value.values.lastIndexOf(lastComplete);

      // Trim the incomplete trailing period, but keep the full series when no
      // complete period exists (lastIndexOf → -1, e.g. a newly launched client)
      // so month 1 still renders instead of an empty graph.
      if (lastCompleteIndex !== -1) {
        values = times.value.values.slice(0, lastCompleteIndex + 1);
      }
    }

    return {
      ...times.value,
      values: values.map(value => moment.unix(value).utc().format(timeSeriesFormat.value)),
    };
  });

  // The last period the chart actually plots, mirroring the trim in `timeGroupDataset`.
  // `last_complete` is null until a period has fully elapsed, and `moment.unix(null)` is the epoch.
  const titleTimestamp = computed(() => {
    const values = times.value?.values || [];
    const lastComplete = times.value?.calculated?.last_complete;

    return values.includes(lastComplete) ? lastComplete : values[values.length - 1];
  });

  const timeGroupTitle = computed(() => {
    if (!times.value || titleTimestamp.value === undefined) {
      return '';
    }

    if (timeSeries.value === TIME_SERIES.WEEK_TO_WEEK) {
      return `For the week ${moment.unix(titleTimestamp.value).utc().isoWeekday(1).format('MMMM Do, YYYY')}`;
    } else {
      return `In ${moment.unix(titleTimestamp.value).utc().format('MMMM')}`;
    }
  });

  const datasetToTimeGroupedDataset = (dataset, formatter = value => value) => {
    if (!dataset.value) {
      return null;
    }

    if (Array.isArray(dataset.value)) {
      return dataset.value.map(dataset => ({
        ...dataset,
        values: sliceValuesToMatchTime(dataset.values, dataset.calculated).map(value => formatter(value)),
      }));
    }

    const values = sliceValuesToMatchTime(dataset.value.values, dataset.value.calculated).map(value =>
      formatter(value),
    );

    return {
      ...dataset.value,
      calculated: {
        last: values[values.length - 1],
        prev: values[values.length - 2],
      },
      values,
    };
  };

  const getPrevDiffText = (timeGroupedDataset, formatter = value => value, noun = '') => {
    if (!timeGroupedDataset.value) {
      return '';
    }

    const { last, prev } = timeGroupedDataset.value.calculated;

    if (prev === undefined) {
      return '';
    }

    const diff = formatter(Math.abs(last - prev));

    let comparison;
    if (last === prev) {
      comparison = 'the same as';
    } else {
      const direction = last > prev ? 'more' : noun ? 'fewer' : 'less';
      comparison = [diff, direction, noun].filter(Boolean).join(' ');
    }

    return `That is ${comparison} than the previous ${timeSeriesUnit.value}.`;
  };

  const timeSeriesSubtitle = computed(() => `Showing ${timeSeriesUnit.value}-over-${timeSeriesUnit.value} data`);

  const sliceValuesToMatchTime = (values, calculated) => {
    const timeLength = timeGroupDataset.value.values.length;

    if (calculated?.last_complete === undefined) {
      return values.slice(0, timeLength);
    }

    // `last_complete` is the series' value (not an index) at its last complete
    // period. lastIndexOf recovers the boundary by value: it never cuts before the
    // true period (complete data is never dropped), but if that value recurs in the
    // in-progress tail an incomplete bucket may remain. A fully robust boundary
    // needs the series' own time grid (cf. the dedicated Courses Started instance).
    const lastCompleteIndex = values.lastIndexOf(calculated.last_complete);
    const completeLength = lastCompleteIndex === -1 ? timeLength : lastCompleteIndex + 1;

    return values.slice(0, Math.min(timeLength, completeLength));
  };

  return {
    timeSeries,
    timeSeriesUnit,
    timeSeriesSubtitle,
    timeGroupDataset,
    timeGroupTitle,
    datasetToTimeGroupedDataset,
    getPrevDiffText,
  };
};
